import {
    Component,
    OnInit,
    Input,
    OnDestroy,
    HostListener,
    inject,
    input,
    output,
    model,
    computed,
    signal,
    DestroyRef,
} from '@angular/core';
import {
    UntypedFormGroup,
    Validators,
    UntypedFormBuilder,
    ValidationErrors,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { DepositForm } from '../../../../shared/configurations/deposit-form.constant';
import { finalize, Subject, takeUntil } from 'rxjs';
import { Grant } from '../../../../shared/interfaces/grant.model';
import { DepositGrantsService } from '../deposit-grants.service';
import { DepositFormDataService } from '../../../deposit-form-data.service';
import { GrowInAnimation, GrowOutAnimation } from '@exl-ng/mulo-common';
import { MatDialog } from '@angular/material/dialog';
import { ConfigurationHandlerService } from '../../../../core/configuration-handler.service';
import { DefaultTranslatePipe } from '../../../../shared/pipes/default-translate.pipe';
import { CustomTranslatePipe } from '../../../../shared/pipes/custom-translate.pipe';
import { HighlightFilterPipe } from '../../../../shared/pipes/highlight-filter.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { ExternalOrganizationFieldComponent } from '../../../../parts/external-organization-field/external-organization-field.component';
import { MatOption } from '@angular/material/core';

import { EspMatAutocompleteA11yDirective } from '../../../../shared/directives/a11y/mat-autocomplete-extension.directive';
import {
    MatAutocompleteTrigger,
    MatAutocomplete,
} from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'esp-new-grant',
    templateUrl: './new-grant.component.html',
    styleUrls: ['./new-grant.component.scss'],
    host: { class: 'esp-new-grant' },
    animations: [GrowOutAnimation, GrowInAnimation],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormField,
        MatInput,
        MatLabel,
        MatAutocompleteTrigger,
        MatAutocomplete,
        EspMatAutocompleteA11yDirective,
        MatOption,
        MatError,
        ExternalOrganizationFieldComponent,
        MatButton,
        TranslateModule,
        HighlightFilterPipe,
        CustomTranslatePipe,
        DefaultTranslatePipe,
    ],
})
export class NewGrantComponent {
    private formBuilder = inject(UntypedFormBuilder);
    private grantService = inject(DepositGrantsService);
    depositFormDataService = inject(DepositFormDataService);
    confirmDialog = inject(MatDialog);
    configurationHandlerService = inject(ConfigurationHandlerService);
    destroyRef = inject(DestroyRef);

    readonly enterNewGrant = model(false);
    readonly allGrants = model<Grant[]>([]);

    newGrant: UntypedFormGroup;
    readonly filterText = signal('');
    readonly filteredGrantIdsGrants = computed(() => {
        const text = this.filterText();
        if (text.length === 0) {
            return this.allGrants();
        }
        return this.allGrants().filter(
            (obj) =>
                obj.grantId
                    .toLowerCase()
                    .indexOf(text.toString().toLowerCase()) !== -1,
        );
    });
    readonly saveInProcess = signal(false);

    @HostListener('keydown.Escape', ['$event']) private keyFunc(event) {
        event.stopPropagation();
        this.close();
    }

    constructor() {
        this.newGrant = this.formBuilder.group({
            grantId: ['', Validators.required],
            grantName: [''],
            funderCode: ['', Validators.required],
            funderName: ['', [Validators.required, this.validateInstitution]],
        });

        this.newGrant
            .get('grantId')
            .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                if (value != null) {
                    this.filterText.set(value);
                }
            });
    }

    get grantId() {
        return this.newGrant?.get('grantId').value;
    }

    get grantName() {
        return this.newGrant?.get('grantName').value;
    }

    get funderCode() {
        return this.newGrant?.get('funderCode').value;
    }

    get funderName() {
        return this.newGrant?.get('funderName').value;
    }

    isValid() {
        return this.newGrant.valid && (!this.funderName || this.funderCode);
    }

    onSubmitNewGrant() {
        if (this.isGrantIdAlreadyExist(this.grantId, this.funderCode)) {
            const grant = this.allGrants().find(
                (g) =>
                    g.grantId === this.grantId &&
                    g.funderCode === this.funderCode,
            );
            this.onGrantSelect(grant);
            return;
        }

        const newGrantAsGrant: Grant = {} as Grant;
        newGrantAsGrant.grantId = this.grantId;
        newGrantAsGrant.grantName = this.grantName;
        newGrantAsGrant.funderCode = this.funderCode;
        newGrantAsGrant.funderName = this.funderName;
        this.saveInProcess.set(true);
        this.grantService
            .addNewGrant(newGrantAsGrant)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.saveInProcess.set(false)),
            )
            .subscribe((data) => {
                newGrantAsGrant.id = data.toString();
                newGrantAsGrant.selfCreated = true;
                this.depositFormDataService.updateGrants(newGrantAsGrant);
                this.allGrants.update((prev) => [...prev, newGrantAsGrant]);
                this.close();
            });
    }

    isGrantIdAlreadyExist(grantId, funderCode): boolean {
        return this.allGrants().some(
            (g) => g.grantId === grantId && g.funderCode === funderCode,
        );
    }

    close() {
        this.resetForm();
        this.enterNewGrant.set(false);
    }

    resetForm() {
        this.newGrant.reset();
    }

    addGrant(item) {
        if (!this.depositFormDataService.isGrantExist(item)) {
            this.depositFormDataService.updateGrants(item);
        }
    }

    onGrantSelect(grant) {
        this.addGrant(grant);
        this.close();
    }

    get numberOfItemsToDisplay() {
        return DepositForm.MAX_DISPLAYED_ITEMS_IN_DROP_DOWN;
    }

    validateInstitution: ValidationErrors | null = () => {
        const institutionValidator = !this.funderName || this.funderCode;
        return institutionValidator
            ? null
            : {
                  validateInstitution: {
                      valid: false,
                  },
              };
    };
}
